import React, { useLayoutEffect } from 'react';

import {
    getAbsolutePagePathInRevision,
    getPageNextSiblingInRevision,
    getPageParentInRevision,
    isRevisionPageParent,
    type Revision,
    type RevisionPage,
    type RevisionPageDocument,
    type RevisionPageGroup,
    RevisionPageType,
    resolvePathInRevision,
} from '@gitbook/hive-core';

type CurrentPageContext = null | {
    revision: Revision;
    currentPage: RevisionPage;
    currentPagePath: string;
};

/**
 * Resolve the current page to render from the current revision and the current URL.
 */
export function useCurrentHivePage(
    revision: Revision | null,
    urlPagePath: string
): ReturnType<typeof resolveCurrentPage> {
    const currentContextRef = React.useRef<CurrentPageContext>(null);

    const args = React.useMemo(
        () => resolveCurrentPage(revision, urlPagePath, currentContextRef.current),
        [revision, urlPagePath]
    );
    const [currentPage] = args;

    // Keep track of the current page so that we can fallback to it next time if needed
    useLayoutEffect(() => {
        if (revision && currentPage) {
            currentContextRef.current = {
                revision,
                currentPage,
                currentPagePath: urlPagePath,
            };
        }
    }, [revision, currentPage, urlPagePath]);

    return args;
}

/**
 * Resolve current page in the revision:
 *  - When page is being deleted, fallback to its parent.
 *  - When page is a group, resolve to its first inner content
 *  - When path is not a matching a page, we try to resolve to a deprecated navigation path
 *    See https://github.com/GitbookIO/gitbook-x/pull/2007
 */
function resolveCurrentPage(
    revision: Revision | null,
    currentPagePath: string,
    previousContext: CurrentPageContext
): [null, null] | [RevisionPageDocument, string | null] {
    if (!revision) {
        return [null, null];
    }

    let currentPage = resolvePathInRevision(revision, currentPagePath);

    // If the page doesn't exist, we try to find a fallback
    if (!currentPage) {
        // Page doesn't exist, but path changed (probably a user navigation), we can return a "Not found"
        if (!previousContext || previousContext.currentPagePath !== currentPagePath) {
            return [null, null];
        }

        const fallbackPage =
            // Page doesn't exist, and the path is still the same: the page has been deleted or moved
            revision.pages[previousContext.currentPage.id] ||
            // Page has been deleted, we find a parent to fallback to
            getFallbackPage(previousContext.revision, revision, previousContext.currentPage.id);
        if (!fallbackPage || !isRevisionPageParent(fallbackPage)) {
            return [null, null];
        }

        currentPage = fallbackPage;
    }

    // If the page is a group, we resolve to its first inner content
    if (currentPage.type === RevisionPageType.Group) {
        currentPage = getGroupFallbackPage(revision, currentPage);

        if (!currentPage) {
            return [null, null];
        }
    }

    const optimizedPath = getAbsolutePagePathInRevision(revision, currentPage.id);
    return [currentPage, optimizedPath !== currentPagePath ? optimizedPath : null];
}

/**
 * Get a path to redirect the user to when the current page no longer exists.
 */
function getFallbackPage(
    previousRevision: Revision,
    currentRevision: Revision,
    pageKey: string
): RevisionPageDocument | RevisionPageGroup | null {
    const fallbackPage = currentRevision.pages[pageKey];

    // Did page move ?
    if (fallbackPage && isRevisionPageParent(fallbackPage)) {
        return fallbackPage;
    }

    // Otherwise find the deepest ancestor that still exists.
    const parent = getPageParentInRevision(previousRevision, pageKey);
    if (parent) {
        return getFallbackPage(previousRevision, currentRevision, parent.id);
    }

    // If page is root, we default to the first sheet
    const firstPageKey = currentRevision.rootPages.find(
        (rootPageKey) => currentRevision.pages[rootPageKey].type === RevisionPageType.Document
    );

    if (!firstPageKey) {
        return null;
    }

    // @ts-ignore page exists and is a sheet
    return currentRevision.pages[firstPageKey];
}

/**
 * Get a fallback page when the current page is a group.
 */
function getGroupFallbackPage(
    revision: Revision,
    currentPage: RevisionPageGroup
): RevisionPageDocument | null {
    const firstPageKey = currentPage.pages.find(
        (pageKey) => revision.pages[pageKey]?.type === RevisionPageType.Document
    );

    if (firstPageKey) {
        return revision.pages[firstPageKey] as RevisionPageDocument;
    }

    // If group is empty, we go to the next sibling of the
    const nextSibling = getPageNextSiblingInRevision(revision, currentPage.id, {
        filterPage: (page) => page.type !== RevisionPageType.Link,
    });

    if (nextSibling?.type === RevisionPageType.Document) {
        return nextSibling;
    }
    if (nextSibling?.type === RevisionPageType.Group) {
        return getGroupFallbackPage(revision, nextSibling);
    }

    return null;
}
